/**
 * Unified Configuration Validation Hook
 * Main export that combines general and sensor validation
 */
import { useCallback } from 'react';
import type { FieldError, ValidationResult } from '../../types/validation';
import { useGeneralValidation } from './general';
import { useSensorValidation } from './sensor';
import { buildLiteSensorData } from '../../utils/sensorDataHelpers';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

/**
 * Main configuration validation hook
 * Supports both Lite and Mini device types
 */
export const useConfigValidation = (deviceType: DeviceType) => {
  const { validateGeneralConfig } = useGeneralValidation(deviceType);
  const { validateSensorConfig } = useSensorValidation(deviceType);

  /**
   * Validate all configuration data (general + sensors)
   */
  const validateAll = useCallback(
    (deviceConfigData: Record<string, unknown>, curDevice: any): ValidationResult => {
      // Validate general configuration
      const { errors: generalErrors, hints: generalHints } =
        validateGeneralConfig(deviceConfigData);

      if (deviceType === DeviceType.LITE) {
        // Lite: validate multiple sensors
        const deviceSensors =
          (curDevice?.sensor_type as Array<{ value: number; label: string }>) || [];
        const configSensors =
          (deviceConfigData.sensor_type as Array<{ value: number; label: string }>) || [];

        const sensors = Array.from({ length: 5 }, (_, index) => {
          const deviceSensor = deviceSensors[index];
          const configSensor = configSensors[index];

          if (deviceSensor) {
            return deviceSensor;
          } else if (configSensor) {
            return configSensor;
          } else {
            return { value: index, label: `Sensor ${index + 1}` };
          }
        });

        const allSensorErrors: Record<number, Record<string, FieldError>> = {};
        const allSensorHints: Record<number, Record<string, string>> = {};

        sensors.forEach((sensor, index) => {
          // 렌더링과 동일한 평탄화 헬퍼를 사용해 검증 입력을 만든다.
          const sensorData = buildLiteSensorData(deviceConfigData, sensor, index);

          const { errors, hints } = validateSensorConfig(sensorData);
          allSensorErrors[index] = errors;
          allSensorHints[index] = hints;
        });

        return {
          generalErrors,
          generalHints,
          sensorErrors: allSensorErrors,
          sensorHints: allSensorHints,
        };
      } else {
        // Mini: validate single sensor
        if (curDevice?.equipped_sensor != null && curDevice.equipped_sensor !== 255) {
          const sensorData = {
            index: 0,
            name: 'Sensor',
            type: {
              value: curDevice.equipped_sensor,
              label: curDevice.equipped_sensor,
            },
            gas_conc_unit: { value: 0, label: 'PPM' },
            calibration_gas_conc: deviceConfigData.calibration_gas_conc,
            calibration_interval: Array.isArray(deviceConfigData.calibration_interval)
              ? deviceConfigData.calibration_interval[0]
              : deviceConfigData.calibration_interval,
            bump_interval: Array.isArray(deviceConfigData.bump_interval)
              ? deviceConfigData.bump_interval[0]
              : deviceConfigData.bump_interval,
            low_alarm: deviceConfigData.low_alarm,
            high_alarm: deviceConfigData.high_alarm,
            twa_alarm: deviceConfigData.twa_alarm,
            stel_alarm: deviceConfigData.stel_alarm,
            twa_period: deviceConfigData.twa_period,
            stel_period: deviceConfigData.stel_period,
            auto_fresh_air_cal: (deviceConfigData.auto_fresh_air_cal as boolean) || false,
            custom_lel: 1,
          };

          const { errors: sensorErrors, hints: sensorHints } = validateSensorConfig(sensorData);

          return {
            generalErrors,
            generalHints,
            sensorErrors,
            sensorHints,
          };
        } else {
          // No sensor connected
          return {
            generalErrors,
            generalHints,
            sensorErrors: {},
            sensorHints: {},
          };
        }
      }
    },
    [deviceType, validateGeneralConfig, validateSensorConfig]
  );

  return {
    validateAll,
  };
};

// Re-export sub-modules for direct access if needed
export * from './helpers';
export * from './general';
export * from './sensor';
